/**
 * `YYYY-MM-DDThh:mm:ss[.fraction](Z|±hh:mm)`, the RFC 3339 date-time form.
 * `T` and `Z` may be lowercase, as the RFC allows. Hour 24, minute 60 and leap seconds are out.
 */
const RFC3339_DATE_TIME =
  /^(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])[Tt]([01]\d|2[0-3]):([0-5]\d):([0-5]\d)(?:\.(\d+))?([Zz]|[+-](?:[01]\d|2[0-3]):[0-5]\d)$/;

const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

const isLeapYear = (year: number): boolean =>
  year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);

const hasDay = (year: number, month: number, day: number): boolean =>
  day <= (month === 2 && isLeapYear(year) ? 29 : DAYS_IN_MONTH[month - 1]);

/**
 * Strict RFC 3339 date-time parser for untrusted input (peer messages, backend timestamps).
 * `Date.parse` alone is too forgiving: it reads `"1"` as a year and rolls 30 February over to March.
 *
 * Fractional seconds of any length are accepted and cut to milliseconds.
 * Returns `undefined` for anything that is not a real instant.
 */
export const parseRfc3339 = (value: unknown): Date | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const match = RFC3339_DATE_TIME.exec(value);

  if (!match) {
    return undefined;
  }

  const [, year, month, day, hour, minute, second, fraction = '', offset] = match;

  if (!hasDay(Number(year), Number(month), Number(day))) {
    return undefined;
  }

  // The ECMAScript date-time format takes exactly three fraction digits
  const millis = `${fraction}000`.slice(0, 3);
  const parsed = new Date(
    `${year}-${month}-${day}T${hour}:${minute}:${second}.${millis}${offset.toUpperCase()}`,
  );

  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

/** UTC with whole seconds, e.g. `2026-06-12T12:01:00Z`: the form the mobile apps emit */
export const formatRfc3339Utc = (date: Date): string =>
  date.toISOString().replace(/\.\d{3}Z$/, 'Z');
